import styled, { css } from 'styled-components';
import { AutosizeInput } from 'venn-components';
import { GetColor } from 'venn-ui-kit';
import type { Page } from 'venn-components';
import TextareaAutosize from 'react-textarea-autosize';
import { useRecoilCallback, useSetRecoilState } from 'recoil';
import { useCallback } from 'react';
import { selectedBlocksState, viewPages, type BlockId } from 'venn-state';
import type { Layout, LayoutItem } from 'react-grid-layout';

export const useUpdatePage = (pageNumber: number) => {
  const setPages = useSetRecoilState(viewPages);

  const updatePage = useCallback(
    (pageUpdates: Partial<Page>) => {
      setPages((pages) =>
        pages.map((page, index) =>
          index === pageNumber
            ? ({
                ...page,
                ...pageUpdates,
              } as Page)
            : page,
        ),
      );
    },
    [pageNumber, setPages],
  );

  return updatePage;
};

// Shared grid-page primitives (used by GridPage & ContinuationGridPage)
export const DROP_ITEM_ID = 'dropping_item';
export const DROPPING_ITEM = { i: DROP_ITEM_ID, w: 2, h: 5 };

export const useOnDragOrResize = (blockIds: readonly BlockId[]) =>
  useRecoilCallback(
    ({ set }) =>
      (_layout: Layout, _oldItem: LayoutItem | null, newItem: LayoutItem | null) => {
        if (!newItem) {
          return;
        }
        const blockId = blockIds.find((id) => newItem.i === id);
        blockId && set(selectedBlocksState, (current) => (current.includes(blockId) ? current : [blockId]));
      },
    [blockIds],
  );

export const InlineInput = styled(AutosizeInput)<{ $inPrintMode: boolean; placeholderColor?: string }>`
  background-color: transparent;
  color: unset;
  border: none;
  border-radius: 4px;

  :hover,
  :focus {
    outline: 1px solid ${GetColor.Primary.Main};
  }

  ${({ placeholderColor }) =>
    placeholderColor &&
    css`
      ::placeholder {
        color: ${placeholderColor};
        opacity: 0.75;
      }
    `}

  ${({ $inPrintMode }) =>
    $inPrintMode &&
    css`
      ::placeholder {
        color: transparent;
      }
    `}
`;

export const MultilineInput = styled(TextareaAutosize)<{
  inPrintMode: boolean;
}>`
  display: block !important;
  transform: translateX(10px);
  border-color: transparent;
  resize: none;
  width: 100%;
  text-align: right;

  :hover {
    outline: 1px solid ${GetColor.Primary.Main};
  }

  :focus {
    outline: 1px solid transparent;
  }

  ${({ inPrintMode }) =>
    inPrintMode &&
    css`
      resize: none;

      ::placeholder {
        color: transparent;
      }
    `}
`;

export const CoverPageRow = styled.div<{ height: string }>`
  position: relative;
  display: block;
  font-size: 30px;
  text-align: left;
  height: ${(props) => props.height ?? '33%'};
  border: 0;
  padding: 0;
  margin: 0;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;

  textarea {
    background: transparent;
  }
`;

export const CoverPageOrganizationName = styled.span`
  vertical-align: text-bottom;
  margin: 0;
  padding: 0;
  text-align: right;
  font-weight: bold;
`;

export const CoverPageBackground = styled.div<{ src: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background-image: url(${(props) => props.src});
  background-size: cover;
  background-position: center;
`;
